"use client";

import type { ReactNode } from "react";
import Link from "next/link";
import { ChevronRight, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * /my 인덱스 대시보드의 위젯 카드 프리미티브 (JCG-2073).
 * 헤더(아이콘 + 제목, 둘 다 선택) + 우측 "전체 보기" 링크 + 본문. 디자인 토큰 + amber 액센트만 사용.
 * 제목을 생략하면(예: 자체 제목이 있는 정답률 차트) 헤더엔 링크만 우측 정렬된다.
 */
export default function DashboardCard({
  icon: Icon,
  title,
  href,
  linkLabel = "전체 보기",
  children,
  className,
}: {
  icon?: LucideIcon;
  title?: string;
  href?: string;
  linkLabel?: string;
  children: ReactNode;
  className?: string;
}) {
  const showHeader = Boolean(title || href);
  return (
    <section
      className={cn("rounded-xl border border-border bg-card p-4 sm:p-5", className)}
    >
      {showHeader && (
        <div className="mb-3 flex items-center justify-between gap-2">
          {title ? (
            <div className="flex min-w-0 items-center gap-2">
              {Icon && (
                <Icon className="h-[18px] w-[18px] shrink-0 text-amber-500" aria-hidden />
              )}
              <h2 className="truncate text-sm font-bold text-foreground">{title}</h2>
            </div>
          ) : (
            <span />
          )}
          {href && (
            <Link
              href={href}
              className="flex shrink-0 items-center gap-0.5 rounded-md text-xs font-semibold text-muted-foreground transition-colors hover:text-amber-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 dark:hover:text-amber-400"
            >
              {linkLabel}
              <ChevronRight className="h-3.5 w-3.5" aria-hidden />
            </Link>
          )}
        </div>
      )}
      {children}
    </section>
  );
}
